"use client";
import { formatGBP } from "../lib/format";
import {
  volumeBreakSavingsPct,
  resolveActiveTier,
  type VolumeBreakTier,
} from "../src/lib/quantity-helpers";

interface Props {
  basePriceMinor: number;
  breaks: ReadonlyArray<VolumeBreakTier>;
  currentQuantity?: number;
}

export function VolumeBreakLadder({
  basePriceMinor,
  breaks,
  currentQuantity,
}: Props) {
  if (breaks.length === 0) return null;
  const sorted = [...breaks].sort((a, b) => a.min_quantity - b.min_quantity);
  const activeTier =
    currentQuantity != null ? resolveActiveTier(sorted, currentQuantity) : null;

  // Synthesise the base-price row (qty 1 to first tier - 1).
  const firstTierMin = sorted[0]?.min_quantity ?? 1;
  const showsBaseRow = firstTierMin > 1;

  return (
    <section className="mt-4">
      <h3 className="text-sm font-medium text-ink-700">Volume pricing</h3>
      <table className="mt-2 w-full text-sm">
        <thead>
          <tr className="border-b border-ink-200">
            <th scope="col" className="text-start font-medium text-ink-500">Quantity</th>
            <th scope="col" className="text-end font-medium text-ink-500">Unit price</th>
            <th scope="col" className="text-end font-medium text-ink-500">Save</th>
          </tr>
        </thead>
        <tbody>
          {showsBaseRow && (
            <tr
              className={
                activeTier === null && currentQuantity != null
                  ? "bg-accent-soft"
                  : ""
              }
            >
              <td className="py-1 text-ink-900">
                1–{firstTierMin - 1}
              </td>
              <td className="py-1 text-end text-ink-900">
                {formatGBP(basePriceMinor)}
              </td>
              <td className="py-1 text-end text-ink-500">—</td>
            </tr>
          )}
          {sorted.map((tier, i) => {
            const isLast = i === sorted.length - 1;
            const upperBound = isLast
              ? null
              : sorted[i + 1]!.min_quantity - 1;
            const qtyLabel = upperBound
              ? `${tier.min_quantity}–${upperBound}`
              : `${tier.min_quantity}+`;
            const savings = volumeBreakSavingsPct(
              basePriceMinor,
              tier.unit_price_minor,
            );
            const isActive =
              activeTier?.min_quantity === tier.min_quantity;
            return (
              <tr
                key={tier.min_quantity}
                className={isActive ? "bg-accent-soft" : ""}
              >
                <td className="py-1 text-ink-900">{qtyLabel}</td>
                <td className="py-1 text-end text-ink-900">
                  {formatGBP(tier.unit_price_minor)}
                </td>
                <td className="py-1 text-end text-accent-deep">
                  {savings}%
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </section>
  );
}
